import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save } from 'lucide-react';
import { t } from '../../i18n';
import { Card, Button, Input, Alert } from '../../components/ui';
import { useUnsaved, confirmLeaving } from '../../lib/unsavedChanges';
import { sameFields } from '../../lib/settingsForm';

/**
 * What every Configuración tab is built from.
 *
 * Each card saves with its own Guardar, so a mistake in one card never stops
 * another from saving, and whether it saved, or why not, shows under what was
 * typed instead of at the top of a long page. Guardar stays off until
 * something in the card changes, and a changed card counts as unsaved until it
 * is saved (lib/unsavedChanges.js).
 */

/**
 * A card's fields as typed and as last saved. `save(write, savedText)` hands
 * `write` what was typed. `write` returns an error message to show; nothing once
 * it saved; or `{ saved }` with the values as stored, when saving tidied them (a
 * trimmed name, an emptied field).
 */
export const useSettingsCard = (key, initial) => {
  const [saved, setSaved] = useState(initial);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const dirty = !sameFields(form, saved);
  useUnsaved(key, dirty);

  const change = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
    setNotice(null);
  };

  const save = async (write, savedText) => {
    setError(null);
    setNotice(null);
    setSaving(true);
    let result;
    try {
      result = await write(form);
    } catch (caught) {
      result = caught.message;
    }
    setSaving(false);
    if (typeof result === 'string') { setError(result); return; }
    const stored = result?.saved ? { ...form, ...result.saved } : form;
    if (result?.saved) setForm(stored);
    setSaved(stored);
    setNotice(savedText ?? t('settings.cardSaved'));
  };

  return { form, change, save, card: { dirty, saving, error, notice } };
};

/**
 * `children` is what Guardar saves. `after` goes below it, past a line: things
 * that save by themselves, or lead elsewhere.
 */
const SettingsCard = ({ title, subtitle, card, onSave, children, after }) => (
  <Card title={title} subtitle={subtitle}>
    <div className="space-y-4">
      {children}
      {card.error && <Alert tone="danger">{card.error}</Alert>}
      {card.notice && <Alert tone="success">{card.notice}</Alert>}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {card.dirty && <span className="text-xs font-medium text-amber-700">{t('settings.unsaved')}</span>}
        <Button onClick={onSave} loading={card.saving} disabled={!card.dirty}>
          <Save size={16} />{t('common.save')}
        </Button>
      </div>
      {after && <div className="border-t border-slate-100 pt-4">{after}</div>}
    </div>
  </Card>
);

/** A number field followed by its unit: "[24] horas antes del turno". */
export const NumberWithUnit = ({ unit, className = 'w-28', ...props }) => (
  <div className="flex items-center gap-2">
    <Input type="number" step="1" className={className} {...props} />
    <span className="text-sm text-slate-600">{unit}</span>
  </div>
);

/** A button to another screen, which asks first when a card here is unsaved. */
export const GoButton = ({ to, children }) => {
  const navigate = useNavigate();
  return (
    <Button variant="secondary" onClick={() => { if (confirmLeaving()) navigate(to); }}>
      {children}
    </Button>
  );
};

export default SettingsCard;
